import { useMutation } from '@tanstack/react-query'
import { Check, Minus, Shield, X } from 'lucide-react'
import { useParams } from 'react-router-dom'

import { queryClient } from '@/api/clients'
import { Breadcrumbs } from '@/common/Breadcrumbs'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import { ErrorHandler, formatUtc } from '@/helpers'
import { canVoteOnProposal, formatVoteOption } from '@/helpers/governance'
import { buildVoteMsg, VoteError } from '@/libs/keplr/messages'
import {
  useProposal,
  useProposalVotes,
  useVoterVote,
} from '@/queries/governance'
import { queryKeys } from '@/queries/keys'
import { RoutePaths } from '@/routes'
import { VoteOption } from '@/types/governance'
import UiAddressCopy from '@/ui/UiAddressCopy'
import { UiBadge } from '@/ui/UiBadge'
import { UiCard } from '@/ui/UiCard'
import UiContainer from '@/ui/UiContainer'
import { UiSkeleton } from '@/ui/UiSkeleton'

import GovernanceProposalStatusBadge from './components/GovernanceProposalStatusBadge'

export default function ProposalDetailPage() {
  const { proposalId } = useParams<{ proposalId: string }>()
  const { address, isWalletConnected, signAndBroadcast } = useKeplrWallet()

  const { data: proposalData, isLoading, isError } = useProposal(proposalId)
  const { data: votesData } = useProposalVotes(proposalId, { limit: '100' })
  const { data: userVoteData } = useVoterVote(proposalId, address ?? undefined)

  const proposal = proposalData?.proposal

  const voteMutation = useMutation({
    mutationFn: async (option: VoteOption) => {
      if (!proposalId || !address || !proposal) {
        throw new Error('Missing required data')
      }

      const message = buildVoteMsg({
        proposal_id: proposalId,
        voter: address,
        option,
        metadata: '',
      })

      return signAndBroadcast([message])
    },
    onSuccess: () => {
      if (!proposalId || !address) return

      queryClient.invalidateQueries({
        queryKey: queryKeys.governance.vote(proposalId, address),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.governance.votes(proposalId),
      })
    },
    onError: error => {
      ErrorHandler.process(VoteError.fromUnknown(error))
    },
  })

  if (isLoading) {
    return (
      <UiContainer className='py-8'>
        <div>
          <UiSkeleton className='mb-6 h-10 w-64' />
          <UiCard className='p-6'>
            <UiSkeleton className='mb-4 h-6 w-full' />
            <UiSkeleton className='mb-2 h-4 w-3/4' />
            <UiSkeleton className='h-4 w-1/2' />
          </UiCard>
        </div>
      </UiContainer>
    )
  }

  if (isError || !proposal) {
    return (
      <UiContainer className='py-8'>
        <div>
          <Breadcrumbs
            items={[{ label: 'Governance', to: RoutePaths.Governance }]}
          />
          <UiCard className='mt-6 p-6'>
            <p className='text-error text-center'>
              Failed to load proposal. Please try again later.
            </p>
          </UiCard>
        </div>
      </UiContainer>
    )
  }

  const isVotingActive = canVoteOnProposal(proposal.status)
  const userHasVoted = Boolean(userVoteData?.vote)

  const breadcrumbsItems = [
    { label: 'Home', to: RoutePaths.Root },
    { label: 'Governance', to: RoutePaths.Governance },
    { label: `Proposal #${proposal.id}` },
  ]

  return (
    <UiContainer>
      <div className='mt-26 space-y-6 px-4 md:px-0'>
        <div className='bg-background sticky top-0 z-10 flex flex-col gap-6 pt-2 pb-4'>
          <Breadcrumbs items={breadcrumbsItems} />

          <div className='flex flex-wrap items-start justify-between gap-4'>
            <div className='min-w-0 flex-1 space-y-2'>
              <h1 className='text-foreground text-xl font-semibold md:text-2xl'>
                Proposal #{proposal.id}
              </h1>
            </div>

            <GovernanceProposalStatusBadge status={proposal.status} />
          </div>
        </div>

        {/* Timeline */}
        <UiCard className='p-6'>
          <h2 className='text-foreground mb-4 text-sm font-semibold'>
            Timeline
          </h2>
          <div className='grid grid-cols-2 gap-4 text-sm sm:grid-cols-4'>
            <div className='flex flex-col gap-1'>
              <span className='text-muted-foreground'>Submitted:</span>
              <span className='text-foreground font-medium'>
                {formatUtc(new Date(proposal.submit_time), 'yyyy-MM-dd HH:mm')}
              </span>
            </div>
            <div className='flex flex-col gap-1'>
              <span className='text-muted-foreground'>Deposit ends:</span>
              <span className='text-foreground font-medium'>
                {formatUtc(
                  new Date(proposal.deposit_end_time),
                  'yyyy-MM-dd HH:mm',
                )}
              </span>
            </div>
            <div className='flex flex-col gap-1'>
              <span className='text-muted-foreground'>Voting starts:</span>
              <span className='text-foreground font-medium'>
                {formatUtc(
                  new Date(proposal.voting_start_time),
                  'yyyy-MM-dd HH:mm',
                )}
              </span>
            </div>
            <div className='flex flex-col gap-1'>
              <span className='text-muted-foreground'>Voting ends:</span>
              <span className='text-foreground font-medium'>
                {formatUtc(
                  new Date(proposal.voting_end_time),
                  'yyyy-MM-dd HH:mm',
                )}
              </span>
            </div>
          </div>
        </UiCard>

        {/* Tally Results */}
        {proposal.final_tally_result && (
          <UiCard className='p-6'>
            <h2 className='text-foreground mb-4 text-sm font-semibold'>
              Tally Results
            </h2>
            <div className='grid grid-cols-2 gap-4 sm:grid-cols-4'>
              {(() => {
                const results = proposal.final_tally_result
                const total =
                  BigInt(results.yes_count) +
                  BigInt(results.no_count) +
                  BigInt(results.abstain_count) +
                  BigInt(results.no_with_veto_count)

                const calculatePercentage = (count: string) => {
                  if (total === BigInt(0)) return 0
                  return Number((BigInt(count) * BigInt(100)) / total)
                }

                const items = [
                  {
                    label: 'Yes',
                    count: results.yes_count,
                    textColor: 'text-[hsl(var(--chart-1))]',
                    icon: Check,
                  },
                  {
                    label: 'No',
                    count: results.no_count,
                    textColor: 'text-[hsl(var(--chart-5))]',
                    icon: X,
                  },
                  {
                    label: 'Abstain',
                    count: results.abstain_count,
                    textColor: 'text-muted-foreground',
                    icon: Minus,
                  },
                  {
                    label: 'Veto',
                    count: results.no_with_veto_count,
                    textColor: 'text-foreground',
                    icon: Shield,
                  },
                ]

                return items.map(item => {
                  const percentage = calculatePercentage(item.count)
                  const Icon = item.icon

                  return (
                    <div
                      key={item.label}
                      className='bg-secondary/50 flex flex-col gap-3 rounded-lg p-4'
                    >
                      <div className='flex items-center gap-2'>
                        <Icon className={`h-4 w-4 ${item.textColor}`} />
                        <span className='text-muted-foreground text-sm'>
                          {item.label}
                        </span>
                      </div>
                      <div className='flex flex-col gap-1'>
                        <span className='text-foreground text-2xl font-semibold'>
                          {percentage.toFixed(1)}%
                        </span>
                        <span className='text-muted-foreground text-xs'>
                          {item.count}
                        </span>
                      </div>
                    </div>
                  )
                })
              })()}
            </div>
          </UiCard>
        )}

        {/* Voting Section */}
        {isVotingActive && (
          <UiCard className='p-6'>
            <h2 className='text-foreground mb-4 text-sm font-semibold'>
              Cast Your Vote
            </h2>

            {(() => {
              if (!isWalletConnected) {
                return (
                  <p className='text-muted-foreground text-sm'>
                    Please connect your wallet to vote on this proposal.
                  </p>
                )
              }

              if (userHasVoted && userVoteData) {
                return (
                  <div className='space-y-2'>
                    <p className='text-success text-sm font-medium'>
                      You have already voted on this proposal.
                    </p>

                    <div className='flex items-center gap-2'>
                      <span className='text-muted-foreground text-sm'>
                        Your vote:
                      </span>

                      <UiBadge variant='outline' size='sm'>
                        {userVoteData.vote.options
                          .map(opt => formatVoteOption(opt.option))
                          .join(', ')}
                      </UiBadge>
                    </div>
                  </div>
                )
              }

              return (
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                  <button
                    onClick={() => voteMutation.mutate(VoteOption.Yes)}
                    disabled={voteMutation.isPending}
                    className='border-border hover:border-foreground/20 hover:bg-secondary/50 group flex items-center gap-4 rounded-lg border p-4 transition-all disabled:pointer-events-none disabled:opacity-50'
                  >
                    <div className='bg-secondary/50 group-hover:bg-secondary flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors'>
                      <Check className='h-6 w-6 text-[hsl(var(--chart-1))]' />
                    </div>
                    <div className='flex flex-1 flex-col items-start gap-1'>
                      <span className='text-foreground font-medium'>Yes</span>
                      <span className='text-muted-foreground text-xs'>
                        Vote in favor
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => voteMutation.mutate(VoteOption.No)}
                    disabled={voteMutation.isPending}
                    className='border-border hover:border-foreground/20 hover:bg-secondary/50 group flex items-center gap-4 rounded-lg border p-4 transition-all disabled:pointer-events-none disabled:opacity-50'
                  >
                    <div className='bg-secondary/50 group-hover:bg-secondary flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors'>
                      <X className='h-6 w-6 text-[hsl(var(--chart-5))]' />
                    </div>
                    <div className='flex flex-1 flex-col items-start gap-1'>
                      <span className='text-foreground font-medium'>No</span>
                      <span className='text-muted-foreground text-xs'>
                        Vote against
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => voteMutation.mutate(VoteOption.Abstain)}
                    disabled={voteMutation.isPending}
                    className='border-border hover:border-foreground/20 hover:bg-secondary/50 group flex items-center gap-4 rounded-lg border p-4 transition-all disabled:pointer-events-none disabled:opacity-50'
                  >
                    <div className='bg-secondary/50 group-hover:bg-secondary flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors'>
                      <Minus className='text-muted-foreground h-6 w-6' />
                    </div>
                    <div className='flex flex-1 flex-col items-start gap-1'>
                      <span className='text-foreground font-medium'>
                        Abstain
                      </span>
                      <span className='text-muted-foreground text-xs'>
                        Abstain from voting
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => voteMutation.mutate(VoteOption.NoWithVeto)}
                    disabled={voteMutation.isPending}
                    className='border-border hover:border-foreground/20 hover:bg-secondary/50 group flex items-center gap-4 rounded-lg border p-4 transition-all disabled:pointer-events-none disabled:opacity-50'
                  >
                    <div className='bg-secondary/50 group-hover:bg-secondary flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors'>
                      <Shield className='text-foreground h-6 w-6' />
                    </div>
                    <div className='flex flex-1 flex-col items-start gap-1'>
                      <span className='text-foreground font-medium'>Veto</span>
                      <span className='text-muted-foreground text-xs'>
                        Vote with veto
                      </span>
                    </div>
                  </button>
                </div>
              )
            })()}
          </UiCard>
        )}

        {/* Votes List */}
        <UiCard className='p-6'>
          <h2 className='text-foreground mb-4 text-sm font-semibold'>
            Votes ({votesData?.votes?.length ?? 0})
          </h2>

          {(() => {
            if (!votesData?.votes || votesData.votes.length === 0) {
              return (
                <p className='text-muted-foreground py-6 text-center text-sm'>
                  No votes yet.
                </p>
              )
            }

            return (
              <div
                className='grid max-h-[50vh] gap-3 overflow-auto pr-2'
                role='list'
                aria-label='Votes'
              >
                {votesData.votes.map((vote, index) => (
                  <div
                    key={vote.voter}
                    role='listitem'
                    className='bg-muted/30 flex items-center justify-between gap-3 rounded-lg p-3'
                  >
                    <div className='flex min-w-0 flex-1 items-center gap-3'>
                      <div className='bg-primary/10 text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold'>
                        {index + 1}
                      </div>
                      <div className='min-w-0 flex-1'>
                        <UiAddressCopy
                          start={20}
                          end={10}
                          address={vote.voter}
                          className='font-mono text-sm'
                          showFull={false}
                        />
                      </div>
                    </div>
                    <div className='flex shrink-0 flex-wrap gap-2'>
                      {vote.options.map((opt, idx) => (
                        <UiBadge key={idx} variant='outline' size='sm'>
                          {formatVoteOption(opt.option)}
                          {opt.weight !== '1.0' && ` (${opt.weight})`}
                        </UiBadge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          })()}
        </UiCard>
      </div>
    </UiContainer>
  )
}
